import React, { useState } from 'react';
import {
  X, ChevronRight, ChevronLeft, Home, Users, PawPrint, Syringe, Package,
  BarChart3, Settings, CalendarDays, BedDouble, FileText, Wallet, MessageCircle,
  Search, Weight, FlaskConical, ClipboardList, DoorOpen, Send, Zap,
  CreditCard, Bell, ShoppingCart, Pencil, Tag
} from 'lucide-react';

const GUIDES = [
  {
    id: 'getting-started',
    icon: Zap,
    title: 'Hızlı Başlangıç',
    color: 'text-[#6b6de3]',
    bg: 'bg-[#6b6de3]/10',
    steps: [
      {
        title: 'Müşteri Aramak',
        content: 'Ana sayfadaki arama kutusuna müşterinin adını, telefonunu veya TC kimlik numarasını yazın. Tek sonuç kaldığında Enter tuşuna basarak doğrudan müşteri sayfasına gidin.'
      },
      {
        title: 'Yeni Müşteri Eklemek',
        content: 'Ana sayfada "Yeni Müşteri" butonuna tıklayın. Ad, telefon ve TC bilgilerini girin. Aynı anda hayvanını da ekleyebilirsiniz.'
      },
      {
        title: 'Hızlı İşlem Yapmak',
        content: '"Hızlı İşlem" butonuyla müşteriyi seçin, ardından aşı/tedavi/ürün ekleyin. Ödeme yöntemini seçin ve kaydedin. Hepsi tek ekranda.'
      },
      {
        title: 'Kısayollar',
        content: 'Arama sonuçlarında tek eşleşme varken Enter\'a basın → otomatik seçilir. Barkod okutarak ürün ekleyebilirsiniz. Tüm modallarda bu kısayol çalışır.'
      }
    ]
  },
  {
    id: 'operations',
    icon: Syringe,
    title: 'Aşı, Tedavi & Hizmet',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    steps: [
      {
        title: 'İşlem Nasıl Eklenir?',
        content: 'Ana sayfada "Hızlı İşlem" butonuna tıklayın. Müşteriyi arayıp seçin, hastayı seçin. Üstteki sekmelerden Aşı, Tedavi veya Hizmet\'i seçip arama yapın ve ekleyin.'
      },
      {
        title: 'Hatırlatma Tarihi',
        content: 'Aşı eklerken "Sonraki Tarih" otomatik hesaplanır. İsterseniz değiştirebilirsiniz. Bu tarih geldiğinde müşteriye otomatik SMS gönderilir.'
      },
      {
        title: 'Not ve Kilo Eklemek',
        content: 'İşlem kaydederken alttaki "Not Ekle" ve "Kilo Güncelle" seçeneklerini açabilirsiniz. Muayene notu ve güncel kilo aynı anda kaydedilir.'
      },
      {
        title: 'Ödeme Alma',
        content: 'Sepete eklenen işlemlerin toplamı altta görünür. Nakit, kart veya parçalı ödeme seçin. Veresiye bırakırsanız bakiyeye otomatik yansır.'
      }
    ]
  },
  {
    id: 'patient-file',
    icon: ClipboardList,
    title: 'Hasta Dosyası',
    color: 'text-teal-600',
    bg: 'bg-teal-50',
    steps: [
      {
        title: 'Hasta Notları',
        content: 'Müşteri sayfasında hayvanın kartındaki "Notlar" butonuna tıklayın. Metin notu yazın, isterseniz fotoğraf veya PDF dosyası ekleyin. Tüm notlar tarih sırasında listelenir.'
      },
      {
        title: 'Laboratuvar Sonuçları',
        content: '"Laboratuvar" butonuyla lab sonuçları modalını açın. Sonuç tipini seçin (Biyokimya, Hemogram, İdrar vb.), not ekleyin ve dosya yükleyin. Tipe göre filtreleme yapabilirsiniz.'
      },
      {
        title: 'Kilo Takibi',
        content: '"Kilo" butonuyla kilo geçmişini görün. Her kayıtta artış-azalış trendi otomatik hesaplanır. İşlem yaparken de kilo güncelleyebilirsiniz.'
      },
      {
        title: 'Dosya Yükleme',
        content: 'Not ve lab sonuçlarına fotoğraf (JPG, PNG) veya PDF dosyası ekleyebilirsiniz. Dosyalar güvenli şekilde saklanır ve her zaman erişilebilir.'
      }
    ]
  },
  {
    id: 'customers',
    icon: Users,
    title: 'Müşteri Yönetimi',
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    steps: [
      {
        title: 'Müşteri Sayfası',
        content: 'Sol menüden "Müşteriler"e tıklayın. Tüm müşterileriniz listelenir. Arama yapabilir, yeni müşteri ekleyebilirsiniz.'
      },
      {
        title: 'Müşteri Detayı',
        content: 'Müşteriye tıklayarak detay sayfasına gidin. Burada hayvanları, işlem geçmişi, bakiye durumu ve SMS ayarları görünür.'
      },
      {
        title: 'Bakiye ve Borç Takibi',
        content: 'Bakiye butonuyla müşterinin borç/alacak durumunu görün. Tahsilat veya borç ekleyebilirsiniz. Borçlu müşteriye SMS veya WhatsApp ile hatırlatma gönderebilirsiniz.'
      },
      {
        title: 'SMS Ayarları',
        content: 'Müşteri bazında SMS bildirimleri kapatabilirsiniz. Hatta belirli bir hayvan için ayrı ayrı SMS\'i devre dışı bırakabilirsiniz.'
      }
    ]
  },
  {
    id: 'boarding',
    icon: DoorOpen,
    title: 'Konaklama',
    color: 'text-violet-600',
    bg: 'bg-violet-50',
    steps: [
      {
        title: 'Oda ve Kafes Tanımlama',
        content: 'Konaklama sayfasında "Oda Ekle" ile yeni oda oluşturun. Her odaya kafesler ekleyebilirsiniz. Oda tipi (Köpek/Kedi/Genel) belirleyebilirsiniz.'
      },
      {
        title: 'Giriş (Check-in)',
        content: 'Boş bir kafese tıklayın, müşteri ve hayvanı seçin. Giriş saati otomatik kaydedilir. Notlar ekleyebilirsiniz.'
      },
      {
        title: 'Çıkış (Check-out)',
        content: 'Dolu kafesteki hayvanın "Çıkış" butonuna tıklayın. Kalış süresi ve ücret otomatik hesaplanır.'
      },
      {
        title: 'Geçmiş ve Raporlar',
        content: '"Geçmiş" butonuyla tüm konaklama kayıtlarını görün. Personel bazlı filtreleme yapabilir, haftalık geçmişe bakabilirsiniz.'
      }
    ]
  },
  {
    id: 'sales',
    icon: ShoppingCart,
    title: 'Ürün Satışı',
    color: 'text-pink-600',
    bg: 'bg-pink-50',
    steps: [
      {
        title: 'Hızlı Satış',
        content: 'Ana sayfada "Hızlı Satış" butonuna tıklayın. Ürün adı veya barkod ile arayın. Tek sonuç kaldığında Enter ile ekleyin. Adet belirleyip sepete ekleyin.'
      },
      {
        title: 'Barkod ile Ekleme',
        content: 'Barkod okuyucu ile ürünü okutun. Ürün otomatik tanınır ve sepete eklenir. Hızlı kasiyerlik için idealdir.'
      },
      {
        title: 'Müşteri Seçimi',
        content: 'Müşterili satış için müşteri seçin. Veresiye satış yapabilir, bakiyeye yansıtabilirsiniz. Müşterisiz (anonim) satış da mümkündür.'
      }
    ]
  },
  {
    id: 'stock',
    icon: Package,
    title: 'Stok Yönetimi',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    steps: [
      {
        title: 'Ürün Eklemek',
        content: 'Sol menüden "Stok"a gidin. "Yeni Ürün" ile ürün ekleyin. Barkod, fiyat, KDV, minimum stok seviyesi ve son kullanma tarihi girin.'
      },
      {
        title: 'Stok Takibi',
        content: 'Her satışta stok otomatik düşer. Minimum seviyenin altına indiğinde uyarı alırsınız. Stok giriş-çıkış geçmişini görebilirsiniz.'
      },
      {
        title: 'Ürün Bağlama (Bağlantı Sekmesi)',
        content: 'Stok sayfasında "Bağlantı" sekmesine geçin. Burada ürünleri aşı, tedavi veya hizmetlere bağlayabilirsiniz. Örneğin "Nobivac" aşısını "Karma Aşı" işlemine bağlarsanız, her karma aşı yapıldığında stoktan otomatik 1 adet Nobivac düşer. Bu sayede işlem yaptığınızda hem hizmet hem ürün satışı tek seferde gerçekleşir.'
      },
      {
        title: 'Bağlama Mantığı',
        content: 'Bir ürün birden fazla işleme bağlanabilir. Bir işlem de birden fazla ürün tüketebilir. İşlem yapıldığında bağlı ürünlerin stokları otomatik güncellenir. Böylece aşı yaptığınızda ayrıca stok düşürmenize gerek kalmaz.'
      },
      {
        title: 'Fiyat Arama',
        content: 'Ana sayfadaki "Fiyat" butonuyla herhangi bir ürünün fiyatını hızlıca arayabilirsiniz. Müşteri sorunca kasanın başında beklemezsiniz.'
      }
    ]
  },
  {
    id: 'sms',
    icon: MessageCircle,
    title: 'SMS & WhatsApp',
    color: 'text-sky-600',
    bg: 'bg-sky-50',
    steps: [
      {
        title: 'Otomatik Hatırlatmalar',
        content: 'Ayarlar → SMS sekmesinden hangi bildirimlerin otomatik gönderileceğini açın/kapatın. Aşı hatırlatması, randevu hatırlatması gibi seçenekler mevcuttur.'
      },
      {
        title: 'SMS Şablonları',
        content: 'Her bildirim türü için mesaj şablonu düzenleyebilirsiniz. {customer_name}, {pet_name}, {operation_name} gibi değişkenler otomatik doldurulur.'
      },
      {
        title: 'Test SMS Gönderimi',
        content: 'SMS ayarlarında test telefon numarası girin, ardından her şablonun yanındaki "Test Et" butonuyla deneme mesajı gönderin.'
      },
      {
        title: 'Manuel SMS & WhatsApp',
        content: 'Ana sayfadaki yaklaşan hatırlatmalar bölümünde ve bakiye modalında "SMS Gönder" veya "WhatsApp" butonlarıyla tek tuşla mesaj gönderin.'
      }
    ]
  },
  {
    id: 'reports',
    icon: BarChart3,
    title: 'Raporlar',
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    steps: [
      {
        title: 'Ciro Raporu',
        content: 'Sol menüden "Raporlar"a gidin. Günlük, haftalık, aylık ciro görün. Nakit-kart dağılımı, personel bazlı gelir ve trend grafikleri.'
      },
      {
        title: 'İşlem Geçmişi',
        content: 'Ana sayfada aşı, tedavi, konaklama ve satış kartlarına tıklayarak geçmişi görün. Personel ve tarih bazlı filtreleme yapabilirsiniz.'
      },
      {
        title: 'Hasta İstatistikleri',
        content: 'Kısırlaştırma oranları, tür dağılımı, en çok yapılan işlemler gibi raporları raporlar sayfasında bulabilirsiniz.'
      }
    ]
  },
  {
    id: 'customer-panel',
    icon: PawPrint,
    title: 'Müşteri Paneli',
    color: 'text-indigo-600',
    bg: 'bg-indigo-50',
    steps: [
      {
        title: 'Müşteri Paneli Nedir?',
        content: 'Müşterileriniz kendi telefon numarası ve şifresiyle giriş yaparak hayvanlarının bilgilerini, randevularını ve işlem geçmişlerini görebilir.'
      },
      {
        title: 'Giriş Adresi',
        content: 'Müşterilerinize "/customer-login" adresini verin. Telefon ve şifre ile giriş yaparlar. Şifrelerini ilk kayıtta siz belirlersiniz.'
      },
      {
        title: 'Müşteri Ne Görebilir?',
        content: 'Hayvanlarının tüm bilgileri (tür, ırk, yaş, kilo, kısırlaştırma durumu), kilo geçmişi, bağlı olduğu klinik bilgisi, randevuları ve işlem geçmişi.'
      }
    ]
  }
];

const TutorialModal = ({ onClose }) => {
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);

  const guide = GUIDES.find(g => g.id === selectedGuide);

  return (
    <div className="fixed inset-x-0 top-0 bottom-[65px] z-[9998] flex sm:inset-0 sm:items-center sm:justify-center sm:p-3" data-testid="tutorial-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-md lg:max-w-3xl bg-white sm:rounded-2xl shadow-2xl flex flex-col h-full sm:h-auto sm:max-h-[75vh] overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
          {selectedGuide ? (
            <button onClick={() => { setSelectedGuide(null); setCurrentStep(0); }}
              className="flex items-center gap-1.5 text-sm text-[#6b6de3] font-medium hover:text-[#5a5bd0] transition-colors"
              data-testid="tutorial-back-btn">
              <ChevronLeft size={16} />
              Geri
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#6b6de3]/10 rounded-xl flex items-center justify-center">
                <Zap size={16} className="text-[#6b6de3]" />
              </div>
              <div>
                <h2 className="font-bold text-gray-900 text-sm">Kullanım Rehberi</h2>
                <p className="text-[10px] text-[#8E8E93]">Kliniğinizi yönetmek artık çok kolay</p>
              </div>
            </div>
          )}
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors" data-testid="tutorial-close-btn">
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {!selectedGuide ? (
            /* Guide List */
            <div className="p-4 space-y-2" data-testid="tutorial-guide-list">
              {GUIDES.map(g => (
                <button
                  key={g.id}
                  onClick={() => setSelectedGuide(g.id)}
                  className="w-full flex items-center gap-3 p-3.5 rounded-xl border border-gray-100 hover:border-[#6b6de3]/30 hover:bg-[#6b6de3]/[0.02] transition-all group text-left"
                  data-testid={`tutorial-guide-${g.id}`}
                >
                  <div className={`w-10 h-10 ${g.bg} rounded-xl flex items-center justify-center shrink-0`}>
                    <g.icon size={20} className={g.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm">{g.title}</p>
                    <p className="text-[11px] text-[#8E8E93]">{g.steps.length} adım</p>
                  </div>
                  <ChevronRight size={16} className="text-gray-300 group-hover:text-[#6b6de3] transition-colors shrink-0" />
                </button>
              ))}
            </div>
          ) : guide ? (
            /* Step View */
            <div className="p-5" data-testid="tutorial-step-view">
              {/* Guide title */}
              <div className="flex items-center gap-2 mb-5">
                <div className={`w-9 h-9 ${guide.bg} rounded-xl flex items-center justify-center`}>
                  <guide.icon size={18} className={guide.color} />
                </div>
                <h3 className="font-bold text-gray-900">{guide.title}</h3>
              </div>

              {/* Step indicator */}
              <div className="flex gap-1.5 mb-5">
                {guide.steps.map((_, i) => (
                  <button key={i} onClick={() => setCurrentStep(i)}
                    className={`h-1.5 rounded-full transition-all ${i === currentStep ? 'bg-[#6b6de3] flex-[2]' : 'bg-gray-200 flex-1 hover:bg-gray-300'}`}
                    data-testid={`tutorial-step-indicator-${i}`}
                  />
                ))}
              </div>

              {/* Step content */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 bg-[#6b6de3] text-white rounded-lg flex items-center justify-center text-xs font-bold shrink-0">
                    {currentStep + 1}
                  </span>
                  <h4 className="font-bold text-gray-900 text-base">{guide.steps[currentStep].title}</h4>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed pl-8">
                  {guide.steps[currentStep].content}
                </p>
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <button
                  onClick={() => setCurrentStep(s => Math.max(0, s - 1))}
                  disabled={currentStep === 0}
                  className="flex items-center gap-1 text-sm font-medium text-[#8E8E93] hover:text-gray-700 disabled:opacity-30 disabled:cursor-default transition-colors"
                  data-testid="tutorial-prev-step"
                >
                  <ChevronLeft size={16} /> Önceki
                </button>
                <span className="text-xs text-[#8E8E93]">{currentStep + 1} / {guide.steps.length}</span>
                {currentStep < guide.steps.length - 1 ? (
                  <button
                    onClick={() => setCurrentStep(s => s + 1)}
                    className="flex items-center gap-1 text-sm font-medium text-[#6b6de3] hover:text-[#5a5bd0] transition-colors"
                    data-testid="tutorial-next-step"
                  >
                    Sonraki <ChevronRight size={16} />
                  </button>
                ) : (
                  <button
                    onClick={() => { setSelectedGuide(null); setCurrentStep(0); }}
                    className="flex items-center gap-1 text-sm font-medium text-[#34C759] hover:text-green-700 transition-colors"
                    data-testid="tutorial-finish-guide"
                  >
                    Tamamlandı
                  </button>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default TutorialModal;
